import { arrayToMap } from "@/api/query";
import { useScopeReports } from "@/api/report-query";
import { useQuickAccessSites } from "@/api/site-query";
import { useSpaces } from "@/api/space-query";
import { useQuickAccessStations } from "@/api/station-query";
import { useScopeStore } from "@/stores/scope";
import { Coordinates, ObjectSummary } from "@soft-db/monitoring-api-client";
import { storeToRefs } from "pinia";
import { StationStatusStateExtended, useDrawerStore, useLiveStatusStore } from "../../stores";

export type DrawerField = "status" | "object";
export type DrawerFilter = { id: number; field: DrawerField; value: string; name: string };
export type MonitoringObject = "site" | "station" | "report" | "space";
export type ItemObject = {
	object: MonitoringObject;
	id: string;
	name: string;
	description: string;
	linkedObject: ObjectSummary | null;
	connected: boolean;
	spaces: { name: string; id: string }[];
	status: StationStatusStateExtended[];
	coordinates: Coordinates;
	image?: string;
};
export type DrawerSelection = { id: string; type: MonitoringObject };

export const useDrawerData = (objects: MaybeRefOrGetter<MonitoringObject[]>) => {
	const { isFavorites } = storeToRefs(useDrawerStore());

	const {
		sites: allSites,
		isLoading: sitesLoading,
		isPending: sitesPending,
	} = useQuickAccessSites(isFavorites);
	const {
		data: allStations,
		isLoading: stationsLoading,
		isPending: stationsPending,
	} = useQuickAccessStations(isFavorites);

	const { scope } = storeToRefs(useScopeStore());
	const {
		data: allReports,
		isLoading: reportsLoading,
		isPending: reportsPending,
	} = useScopeReports(scope);

	const { spaces: allSpaces, isLoading: spacesLoading, isPending: spacesPending } = useSpaces();
	const spacesMap = computed(() => arrayToMap(allSpaces.value || []));

	const statusStore = useLiveStatusStore();

	const sites = computed<ItemObject[]>(() => {
		const items: ItemObject[] = [];

		for (const site of allSites.value || []) {
			const station = site.station;

			items.push({
				object: "site",
				id: site.id,
				name: site.name,
				description: site.description,
				linkedObject: station ? { id: station.id, name: station.name } : null,
				connected: !!station,
				spaces: site.spaces.map((id) => ({ id, name: spacesMap.value.get(id)?.name || "" })),
				status: statusStore.searchableStatusState("site", site.id),
				coordinates: site.coordinates,
				image: site.imageUrl,
			});
		}

		return items;
	});

	const stations = computed<ItemObject[]>(() => {
		const items: ItemObject[] = [];

		for (const station of allStations.value || []) {
			const site = station.site;

			items.push({
				object: "station",
				id: station.id,
				name: station.name,
				description: station.description,
				linkedObject: site ? { id: site.id, name: site.name } : null,
				connected: !!site,
				spaces: station.space
					? [{ id: station.space, name: spacesMap.value.get(station.space)?.name || "" }]
					: [],
				status: statusStore.searchableStatusState("station", station.id),
				coordinates: station.coordinates,
			});
		}

		return items;
	});

	const reports = computed<ItemObject[]>(() => {
		const items: ItemObject[] = [];

		for (const report of allReports.value || []) {
			const space = spacesMap.value.get(report.spaceId);
			items.push({
				object: "report",
				id: report.id,
				name: report.title,
				description: report.description,
				linkedObject: null,
				connected: report.enabled,
				coordinates: { latitude: 0, longitude: 0 },
				spaces: report.spaceId ? [{ id: report.spaceId, name: space?.name || "" }] : [],
				status: ["none"],
			});
		}

		return items.sort((a, b) => a.name.localeCompare(b.name));
	});

	const spaces = computed<ItemObject[]>(() => {
		const items: ItemObject[] = [];

		for (const space of allSpaces.value || []) {
			items.push({
				object: "space",
				id: space.id,
				name: space.name,
				description: "",
				spaces: [],
				connected: false,
				coordinates: { latitude: 0, longitude: 0 },
				linkedObject: null,
				status: ["none"],
				image: space.logoUrl,
			});
		}

		return items;
	});

	const collection = computed(() => {
		const collection: ItemObject[] = [];

		for (const obj of toValue(objects)) {
			switch (obj) {
				case "site":
					collection.push(...sites.value);
					break;
				case "station":
					collection.push(...stations.value);
					break;
				case "report":
					collection.push(...reports.value);
					break;
				case "space":
					collection.push(...spaces.value);
					break;
			}
		}

		return collection;
	});

	const isLoading = computed(() => {
		return (
			(stationsLoading.value && stationsPending.value) ||
			(sitesLoading.value && sitesPending.value) ||
			(reportsLoading.value && reportsPending.value) ||
			(spacesLoading.value && spacesPending.value)
		);
	});

	return { collection, isLoading };
};
